import { deepseekChat, isDeepSeekConfigured, isOpenAiConfigured, openAiChat } from "@/lib/ai/chatProviders";
import { parseJsonFromLlmContent } from "@/lib/ai/parseLlmJson";
import { LOCALE_LABELS, type SupportedLocale } from "@patient-watch/i18n";
import { z } from "zod";

const TRANSLATION_SYSTEM = `You translate patient-facing questionnaire text for a UK healthcare app.
You get JSON: { "target": "<language>", "fields": [{ "key", "label", "source" }] }.
"label" says what the text is (question, hint, answer option, block title, form name).
Return JSON only: { "translations": { "<key>": "<translation>" } } with one entry for every key.
Keep the meaning exact and the register plain. Keep answer options short and parallel, in the same order.
Keep numbers, units and placeholders as they are. Do not add or explain anything.`;

const responseSchema = z.object({ translations: z.record(z.string(), z.string()) });

/** Batches stay small enough for one reply, even on long questionnaires. */
const BATCH_SIZE = 60;

export function isFormTranslateEnabled(): boolean {
    return isDeepSeekConfigured() || isOpenAiConfigured();
}

export interface FormCopyField {
    key: string;
    label: string;
    source: string;
}

/**
 * Drafts translations for form copy. These are drafts for review: validated questionnaires
 * still need their licensed translation.
 */
export async function translateFormCopy(
    fields: FormCopyField[],
    locale: SupportedLocale
): Promise<Record<string, string>> {
    const chat = isDeepSeekConfigured() ? deepseekChat : isOpenAiConfigured() ? openAiChat : null;
    if (!chat) throw new Error("AI translation is not configured in this environment.");

    const result: Record<string, string> = {};
    for (let start = 0; start < fields.length; start += BATCH_SIZE) {
        const batch = fields.slice(start, start + BATCH_SIZE);
        const content = await chat([
            { role: "system", content: TRANSLATION_SYSTEM },
            {
                role: "user",
                content: JSON.stringify({ target: `${LOCALE_LABELS[locale]} (${locale})`, fields: batch }),
            },
        ]);
        const { translations } = parseJsonFromLlmContent(content, responseSchema);
        for (const field of batch) {
            const value = translations[field.key];
            if (typeof value === "string" && value.trim()) result[field.key] = value.trim();
        }
    }
    return result;
}
